import React, { useMemo } from "react";
import styled from "styled-components";
import { Form, Input, Toggle } from "rsuite";

import CustomField from "components/Form/CustomField";
import Text from "components/Form/Text";

import { formExternalResourceModel } from "./TrunkFormRefactored.utils";

const AUTH_MODE_NONE = "none";
const AUTH_MODE_REGISTER = "register";

const defaultValue = {
    external_fqdn: null,
    external_sip_port: null,
    external_tprefix_out: null,
    external_login: null,
    external_require_registration: false,
    external_password: null,
    external_realm: null
};

const ExternalResourceSection = ({
    formRef,
    disabled,
    initialValues,
    authMode,
    setAuthMode
}) => {
    const model = useMemo(
        () => formExternalResourceModel(authMode === AUTH_MODE_REGISTER),
        [authMode]
    );

    return (
        <Form
            ref={formRef}
            model={model}
            formDefaultValue={{
                ...defaultValue,
                ...initialValues
            }}
            style={{ margin: 0, width: "100%" }}
        >
            <Fields>
                <FieldsRow>
                    <HostField>
                        <Text
                            name="external_fqdn"
                            label="SIP Gateway"
                            placeholder="sip.example.com or 192.0.2.10"
                            disabled={disabled}
                            style={{ minWidth: 220 }}
                        />
                    </HostField>

                    <PortField>
                        <CustomField
                            accepter={Input}
                            name="external_sip_port"
                            label="SIP port"
                            placeholder="5060"
                            disabled={disabled}
                        />
                    </PortField>

                    <TprefixField>
                        <CustomField
                            accepter={Input}
                            name="external_tprefix_out"
                            label="Techprefix"
                            placeholder="123#45"
                            disabled={disabled}
                        />
                    </TprefixField>
                </FieldsRow>

                <ToggleRow>
                    <StyledToggle
                        checked={authMode === AUTH_MODE_REGISTER}
                        checkedChildren="Registration"
                        unCheckedChildren="No registration"
                        disabled={disabled}
                        onChange={(checked) => {
                            setAuthMode(checked ? AUTH_MODE_REGISTER : AUTH_MODE_NONE);
                        }}
                    />
                </ToggleRow>

                <>
                    <CredentialsRow>
                        <CredentialField>
                            <Text
                                name="external_login"
                                label="Login"
                                placeholder="Login"
                                disabled={disabled}
                            />
                        </CredentialField>

                        <CredentialField>
                            <CustomField
                                accepter={Input}
                                name="external_password"
                                label="Password"
                                placeholder="Password"
                                autoComplete="new-password"
                                disabled={disabled}
                            />
                        </CredentialField>
                    </CredentialsRow>

                    <RealmField>
                        <Text
                            name="external_realm"
                            label="Domain"
                            placeholder="Domain"
                            disabled={disabled}
                        />
                    </RealmField>
                </>
            </Fields>
        </Form>
    );
};

export default ExternalResourceSection;

const Fields = styled.div`
    width: min(100%, 660px);
    margin: 0 auto 10px;
    display: flex;
    flex-direction: column;
    align-items: stretch;

    && .rs-form-group {
        display: flex;
        align-items: center;
        margin-bottom: 0;
        width: 100%;
    }

    && .rs-form-group > .rs-form-control-label,
    && .rs-form-group > .rs-control-label {
        /* text-align: right; */
    }

    && .rs-form-group > .rs-form-control-wrapper {
        display: block;
        flex: 1 1 auto;
        min-width: 0;
    }

    .rs-form-control-wrapper,
    .rs-input,
    .rs-input-number {
        width: 100%;
    }

    @media (max-width: 620px) {
        width: 100%;

        && .rs-form-group {
            align-items: flex-start;
            flex-direction: column;
        }

        && .rs-form-group > .rs-form-control-label,
        && .rs-form-group > .rs-control-label {
            margin-bottom: 4px;
            text-align: left;
        }
    }
`;

const ToggleRow = styled.div`
    display: flex;
    justify-content: flex-start;
    margin-bottom: 10px;
    margin-top: 5px;
`;

const FieldsRow = styled.div`
    display: grid;
    grid-template-columns: minmax(280px, 1fr) 145px 185px;
    column-gap: 12px;
    margin-bottom: 10px;

    @media (max-width: 620px) {
        grid-template-columns: minmax(0, 1fr);
        row-gap: 10px;
    }
`;

const CredentialsRow = styled(FieldsRow)`
    grid-template-columns: repeat(2, minmax(0, 1fr));

    @media (max-width: 620px) {
        grid-template-columns: minmax(0, 1fr);
    }
`;

const HostField = styled.div`
    .rs-form-control-label,
    .rs-control-label {
        min-width: 66px;
        white-space: nowrap;
    }
`;

const PortField = styled.div`
    .rs-form-control-label,
    .rs-control-label {
        min-width: 52px;
        white-space: nowrap;
    }
`;

const TprefixField = styled.div`
    .rs-form-control-label,
    .rs-control-label {
        flex: 0 0 72px;
        min-width: 72px;
        margin-right: 8px;
        white-space: nowrap;
    }
`;

const CredentialField = styled.div`
    && {
        .rs-input {
            min-width: 0;
        }
        .rs-form-control-label,
        .rs-control-label {
            min-width: 66px;
            white-space: nowrap;
        }
    }
`;

const RealmField = styled.div`
    margin-bottom: 10px;
    width: 100%;

    .rs-form-control-label,
    .rs-control-label {
        min-width: 66px;
        white-space: nowrap;
    }
`;

const StyledToggle = styled(Toggle)`
    && {
        background-color: var(--color-grey);
        margin: 0;
    }

    &&.rs-btn-toggle .rs-btn-toggle-inner {
       font-size: 14px !important;
       line-height: 1.8;
    }

    &&:hover {
        background-color: var(--color-grey);
    }

    &&.rs-btn-toggle-checked {
        background-color: var(--color-brand1);
    }

    &&.rs-btn-toggle-checked:hover {
        background-color: var(--color-brand1);
    }
`;
